import React from "react";
import "./dsqueuescreen.css"
import {Paths} from "../../app/components/sidebar/Paths";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {AccordionComponent} from "../../app/components/accordion/AccordionComponent";
import {AccordionItemComponent} from "../../app/components/accordion/AccordionItemComponent";
import {
    AccordionHeaderTextComponent,
    AccordionHeaderTextTimeComplexityComponent
} from "../../app/components/accordion/AccordionHeaderTextComponent";
import {CarouselComponent} from "../../app/components/carousel/CarouselComponent";
import {CarouselItemComponent} from "../../app/components/carousel/CarouselItemComponent";
import {ProblemSetComponent} from "../components/problemset/ProblemSet";
import LinkComponent, {ALinkTarget} from "../../app/components/link/LinkComponent";
import {CodeComponent} from "../../app/components/code/CodeComponent";
import QueueImplementationArrayInsertNeg96 from "./assets/implementation/array/QueueImplementationArrayInsertNeg96";
import QueueImplementationArrayInsert12 from "./assets/implementation/array/QueueImplementationArrayInsert12";
import QueueImplementationArrayInsert14 from "./assets/implementation/array/QueueImplementationArrayInsert14";
import QueueImplementationArrayRemoveNeg96 from "./assets/implementation/array/QueueImplementationArrayRemoveNeg96";
import QueueImplementationArrayRemove12 from "./assets/implementation/array/QueueImplementationArrayRemove12";
import QueueImplementationArrayInsertNeg4 from "./assets/implementation/array/QueueImplementationArrayInsertNeg4";
import QueueImplementationArrayInsert0 from "./assets/implementation/array/QueueImplementationArrayInsert0";
import QueueImplementationRotateArrayInsertNeg96
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayInsertNeg96";
import QueueImplementationRotateArrayRemoveNeg96
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayRemoveNeg96";
import QueueImplementationRotateArrayInsertNeg4
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayInsertNeg4";
import QueueImplementationRotateArrayInsert0
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayInsert0";
import QueueImplementationRotateArrayRemove12
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayRemove12";
import QueueImplementationRotateArrayInsert14
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayInsert14";
import QueueImplementationRotateArrayInsert12
    from "./assets/implementation/rotatearray/QueueImplementationRotateArrayInsert12";
import QueueImplementationLinkedListInsertNeg96
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListInsertNeg96";
import QueueImplementationLinkedListInsert12
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListInsert12";
import QueueImplementationLinkedListRemoveNeg96
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListRemoveNeg96";
import QueueImplementationLinkedListInsert14
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListInsert14";
import QueueImplementationLinkedListRemove12
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListRemove12";
import QueueImplementationLinkedListInsertNeg4
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListInsertNeg4";
import QueueImplementationLinkedListInsert0
    from "./assets/implementation/linkedlist/QueueImplementationLinkedListInsert0";
import QueueInsert89 from "./assets/insert/QueueInsert89";
import QueueInsertNeg2 from "./assets/insert/QueueInsertNeg2";
import QueueInsert0 from "./assets/insert/QueueInsert0";
import QueueInsertNeg2Two from "./assets/insert/QueueInsertNeg2Two";
import QueueRemove89 from "./assets/remove/QueueRemove89";
import QueueRemoveNeg2 from "./assets/remove/QueueRemoveNeg2";
import QueueRemove0 from "./assets/remove/QueueRemove0";
import QueueRemoveNeg2Two from "./assets/remove/QueueRemoveNeg2Two";
import {FullCaptionComponent} from "../../app/components/caption/CaptionComponent";
import QueueRemoveNull from "./assets/remove/QueueRemoveNull";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";

export default function DSQueueScreen() {
    return (<ScreenComponent>
        <Header/>
        <Conceptualizing/>
        <Visualizing/>
        <TimeComplexity/>
        <ProblemSet/>
    </ScreenComponent>);
}

function Header() {
    return (<GradientHeaderComponent title={Paths.dataStructures.nested.queue.name}
                                     tagline={Paths.dataStructures.nested.queue.tagline}/>)
}

function Conceptualizing() {
    return (<H2SectionComponent header="Conceptualizing Queues" removeTopPadding={true}>
        <ul>
            <li>A queue is as simple as its name. It's a line of people waiting to check out their groceries.</li>
            <li>The first person in line is the next one called to have their groceries rang up.</li>
            <li>If a new person comes along, they must enter the line at the last spot and wait their turn.</li>
            <li>You often hear queues referred to as "First in, first out" or "FIFO" for short. Which, as we can see,
                simply means the first element added to the queue, will be the first element removed from the queue.
                This is opposite to how <LinkComponent link={Paths.dataStructures.nested.stack.path}
                                                       target={ALinkTarget.internal}>stacks</LinkComponent> work.
            </li>
            <li>We don't really care too much about what's in between the first and last spot.</li>
        </ul>
    </H2SectionComponent>)
}

function Visualizing() {
    return (<H2SectionComponent header="Visualizing Queues">
        <AlertComponent color={ThemeColor.Secondary}>
            <p><strong>NOTE:</strong> A Queue is an <i>abstract</i> data structure. Unlike an Integer, which
                just <i>is</i> a 32-bit number, Queues are really a structure with defined principles that can be
                implemented in many ways.</p>
        </AlertComponent>
        <Principles/>
        <Implementation/>
    </H2SectionComponent>)
}

function Principles() {
    return (<H3SectionComponent header="Prinicples of a Queue" removeTopPadding={true}>
        <ul>
            <li>Elements must be removed in the order they were inserted into the queue.</li>
            <li>An <code>enqueue</code> function must be present that inserts an element at the end of the queue. This
                function may also be named <code>add</code> or <code>addLast</code> depending on the language. The
                naming doesn't matter, so long as the functionality takes in an element and adds it to the end of the
                queue.
            </li>
            <li>A <code>dequeue</code> function must be present that removes an element from the front of the queue.
                This function may also be named <code>remove</code> or <code>removeFirst</code> depending on the
                language. Again, the naming doesn't matter, so long as the functionality removes the first item in the
                queue.
            </li>
        </ul>
    </H3SectionComponent>)
}

function Implementation() {
    return (<H3SectionComponent header="Implementation of a Queue">
        <p>There are several ways to implement a queue. I will not cover all of them, but I will show a few
            examples just to highlight some differences. The exact syntax isn't as important as the foundational
            principles covered in the previous section.
        </p>
        <AccordionComponent>
            <QueueImplementationArray/>
            <QueueImplementationRotateArray/>
            <QueueImplementationLinkedList/>
        </AccordionComponent>
    </H3SectionComponent>)
}

function QueueImplementationArray() {
    const arrayType = "Array<Int>"
    return (<AccordionItemComponent header={<AccordionHeaderTextComponent title="Array" description="The basic way"/>}>

            <p>For example, if we wanted to keep a queue of numbers, we could use
                an <CodeComponent>{arrayType}</CodeComponent>.
            </p>
            <p>To add an Int to our queue, we could simply do <CodeComponent>queue[queue.size] =
                value</CodeComponent>. This would ensure we are always adding our new value to the end of our array,
                which in this case is representing our queue. Because we know the index we want to set our value at,
                we would consider this an <CodeComponent>O(1)</CodeComponent> operation (assuming we are not having
                to resize our array).
            </p>
            <p>Next, we need to worry about our <CodeComponent>dequeue</CodeComponent> operation. We could do
                something like, <CodeComponent>queue.removeAtIndex(0)</CodeComponent>. However, as we saw
                with <LinkComponent link={Paths.dataStructures.nested.array}
                                    target={ALinkTarget.internal}>arrays</LinkComponent>, this could be
                an <CodeComponent>O(n)</CodeComponent> operation as each subsequent item would need to shift over
                one spot in order to fill the gap left behind from removing the first item. We generally want to
                keep time complexity as small as possible. Is there a way we could reduce the time complexity
                from <CodeComponent>O(n)</CodeComponent>?
            </p>
            <p>As we see from our <CodeComponent>enqueue</CodeComponent> operation above, if we could keep track of
                the index of the item we want to remove, we could just <CodeComponent>set</CodeComponent> the value
                at that index to be null, instead of removing the element completely.
                In that case, with <CodeComponent>Array.set()</CodeComponent> operating in
                in <CodeComponent>O(1)</CodeComponent> time, we could reduce the time complexity for
                our <CodeComponent>Queue.dequeue()</CodeComponent> function to also
                be <CodeComponent>O(1)</CodeComponent>. And, because we are always just removing the next item in
                our line, we would only have to keep track of a single Int,
                or an increase of <CodeComponent>O(1)</CodeComponent> space, to know which index we are removing
                next. Sounds like a win-win!
            </p>
            <p>There is one tricky thing you may have missed, though. While this <i>looks</i> like an increase of
                only <CodeComponent>O(1)</CodeComponent> space, we would be constantly allocating new space for our
                array as we continue to add elements. Take a look at the example below.
                <CarouselComponent>
                    <CarouselItemComponent><QueueImplementationArrayInsertNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayInsert12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayInsert14/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayRemoveNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayRemove12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayInsertNeg4/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationArrayInsert0/></CarouselItemComponent>
                </CarouselComponent>
            </p>
            <p>We can see that even though we only ever have at most three (3) elements in our queue at a time, we
                end up allocating 8 spots for it when it's all said and done. We never go back and utilize the space
                at the start that is now sitting empty.
            </p>
        </AccordionItemComponent>
    )
}

function QueueImplementationRotateArray() {
    const arrayType = "Array<Int>"
    return (<AccordionItemComponent
            header={<AccordionHeaderTextComponent title="Rotating Array" description="Fixing the space problem"/>}>
            <p>In our array example above, we found out that we could use <CodeComponent>O(1)</CodeComponent> space
                to keep track of the index of the item we want to remove to minimize time complexity. Similarly, we
                could keep track of index of the item we want to <i>insert</i> to help us minimize space complexity.
            </p>
            <p>So long as the array is large enough for the amount of items in our queue, we could could just keep
                going back to the start of the array and filling in empty elements with new elements. If we
                always know which index has the first item in our queue, and which item has our last item in our
                queue, we can infer that all items between the two indices are the remaining elements in our queue,
                in the order in which they were inserted. Take a look at the example below.
                <CarouselComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayInsertNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayInsert12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayInsert14/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayRemoveNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayRemove12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayInsertNeg4/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationRotateArrayInsert0/></CarouselItemComponent>
                </CarouselComponent>
            </p>
            <p>While we are still using an <CodeComponent>{arrayType}</CodeComponent>, here, we can see we solved
                the problem of allocating more space by keeping track of the insert and delete index.
            </p>
        </AccordionItemComponent>
    )
}

function QueueImplementationLinkedList() {
    const linkedListType = "LinkedList<Int>"
    return (<AccordionItemComponent
            header={<AccordionHeaderTextComponent title="LinkedList" description="Fixing the looping problem"/>}>
            <p>There is still one more catch. What if, as we are looping around, we realize we suddenly have more
                elements than before, and we need to add more space to our array? Do we double the size and then
                re-insert starting at index-0 in order to make sure we keep the FIFO order? We definitely could, but
                as
                you may have already picked up on from the pointers, it may just be easier to remove the overhead of
                the
                array entirely, and use a <CodeComponent>{linkedListType}</CodeComponent>, instead.
            </p>
            <p>We are already keeping track of the "insert" and "delete" indexes in our above examples, this lends
                itself really well to
                a <CodeComponent>head</CodeComponent> and <CodeComponent>tail</CodeComponent> pointer for a
                LinkedList
                instead, where <CodeComponent>head</CodeComponent> keeps track of the first item in our Queue, or
                the
                item we want to remove next, and <CodeComponent>tail</CodeComponent> keeps track of the last item in
                our
                Queue, or the item we want to insert after.
            </p>
            <p>We haven't covered LinkedLists yet, but the idea is that instead of moving to the next index + 1
                spot in the array to determine the next element, we would just use a "next" pointer at each spot
                that tells us which item comes after the current node. While I will cover this in more detail in a
                future course, it's worth calling out now as it solves the issue of running out of space during the
                middle of a rotated array. Take a look at the example below.
                <CarouselComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListInsertNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListInsert12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListInsert14/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListRemoveNeg96/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListRemove12/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListInsertNeg4/></CarouselItemComponent>
                    <CarouselItemComponent><QueueImplementationLinkedListInsert0/></CarouselItemComponent>
                </CarouselComponent>
            </p>
        </AccordionItemComponent>
    )
}

function TimeComplexity() {
    return (<H2SectionComponent header="What is the time complexity?">
        <AccordionComponent>
            <EnqueueTimeComplexity/>
            <DequeueTimeComplexity/>
            <PeekTimeComplexity/>
        </AccordionComponent>
    </H2SectionComponent>)
}

function EnqueueTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Enqueue" timeComplexity="O(1)"/>}>

        <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
            <CarouselItemComponent><QueueInsert89/></CarouselItemComponent>
            <CarouselItemComponent><QueueInsertNeg2/></CarouselItemComponent>
            <CarouselItemComponent><QueueInsert0/></CarouselItemComponent>
            <CarouselItemComponent><QueueInsertNeg2Two/></CarouselItemComponent>
        </CarouselComponent>

        {activeIndex === 0 && <QueueInsert89FullCaption/>}
        {activeIndex === 1 && <QueueInsertNeg2FullCaption/>}
        {activeIndex === 2 && <QueueInsert0FullCaption/>}
        {activeIndex === 3 && <QueueInsertNeg2TwoFullCaption/>}
    </AccordionItemComponent>)
}

function QueueInsert89FullCaption() {
    return (<FullCaptionComponent title="VALUE: 89">
        <p>Inserting a new element into a Queue is quite simple, especially when using LinkedLists as we don't have to
            worry about space allocation or time complexity worries.</p>
        <p>Just like at a grocery store, if a new value wants to be inserted into the queue, it must go to the back of
            the line. In this case, there is no line yet, so 89 just goes to the front, and only spot, in the queue.</p>
    </FullCaptionComponent>)
}

function QueueInsertNeg2FullCaption() {
    return (<FullCaptionComponent title="VALUE: -2">
        <p>Again, all we must do is ensure -2 is inserted at the end of the queue. So, -2 stands behind 89 in line.</p>
    </FullCaptionComponent>)
}

function QueueInsert0FullCaption() {
    return (<FullCaptionComponent title="VALUE: 0">
        <p>Again, all we must do is ensure 0 is inserted at the end of the queue. So, 0 stands behind -2 in line.</p>
    </FullCaptionComponent>)
}

function QueueInsertNeg2TwoFullCaption() {
    return (<FullCaptionComponent title="VALUE: -2">
        <p>There are no rules about duplicates. The only thing we must worry about is inserting the element at the end
            of the queue. So, as we can see, -2 will stand behind the previous value of 0 and we are good to go.</p>
    </FullCaptionComponent>)
}

function DequeueTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Dequeue" timeComplexity="O(1)"/>}>

        <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
            <CarouselItemComponent><QueueRemove89/></CarouselItemComponent>
            <CarouselItemComponent><QueueRemoveNeg2/></CarouselItemComponent>
            <CarouselItemComponent><QueueRemove0/></CarouselItemComponent>
            <CarouselItemComponent><QueueRemoveNeg2Two/></CarouselItemComponent>
            <CarouselItemComponent><QueueRemoveNull/></CarouselItemComponent>
        </CarouselComponent>

        {activeIndex === 0 && <QueueRemove89FullCaption/>}
        {activeIndex === 1 && <QueueRemoveNeg2FullCaption/>}
        {activeIndex === 2 && <QueueRemove0FullCaption/>}
        {activeIndex === 3 && <QueueRemoveNeg2TwoFullCaption/>}
        {activeIndex === 4 && <QueueRemoveNullFullCaption/>}
    </AccordionItemComponent>)
}

function QueueRemove89FullCaption() {
    return (<FullCaptionComponent title="VALUE: 89">
        <p>Removing an element from a Queue is quite simple, especially when using LinkedLists as we don't have to
            worry about space allocation or time complexity worries.</p>
        <p>Just like at a grocery store, the first person in line - 89, in this case - will be the next up to have their
            items checked out. We remove 89 from our queue, or in this case our grocery line.</p>
    </FullCaptionComponent>)
}

function QueueRemoveNeg2FullCaption() {
    return (<FullCaptionComponent title="VALUE: -2">
        <p>Just like we did with our previous value, we are going to continue to remove the first person in line.
            Here, we will remove -2.</p>
        <p>Even though there is another -2 in the queue, we only care about the very first element in our queue, or
            the first instance of -2 in this case.</p>
    </FullCaptionComponent>)
}

function QueueRemove0FullCaption() {
    return (<FullCaptionComponent title="VALUE: 0">
        <p>Just like we did with our previous value, we are going to continue to remove the first person in line.
            Here, we will remove 0.</p>
    </FullCaptionComponent>)
}

function QueueRemoveNeg2TwoFullCaption() {
    return (<FullCaptionComponent title="VALUE: -2">
        <p>Just like we did with our previous value, we are going to continue to remove the first person in line.
            Here, we will remove -2.</p>
    </FullCaptionComponent>)
}

function QueueRemoveNullFullCaption() {
    return (<FullCaptionComponent title="VALUE: null">
        <p>You should always prevent edge cases of a user or program trying to remove an element from a queue that
            doesn't exist.</p>
        <p>If a user were to try to call <CodeComponent>dequeue</CodeComponent> with no one standing in line, we
            should first check that there <i>is</i> a person in line first before we try checking out, or removing
            them, from the queue.</p>
    </FullCaptionComponent>)
}

function PeekTimeComplexity() {
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Peek" timeComplexity="O(1)"/>}>
        <ul>
            <li>In some use cases, it may be helpful to know what is the first element in our queue.</li>
            <li>We may end up using a queue for Merge Sort, or maybe we only want to remove an item from the queue if
                it's reached a certain threshold. In both cases, we would need to know what that very first item is in
                our queue before removing it.
            </li>
            <li>If we call <CodeComponent>dequeue</CodeComponent> we will get the value returned to us, but it will also
                remove it from the queue. If we do our check at that point, it will be too late. We couldn't just easily
                add it back to the queue, because we know calling <CodeComponent>enqueue</CodeComponent> would add it to
                the <i>end</i> of our queue, where we would want it at the front of the queue still.
            </li>
            <li>Therefore, we introduce a <CodeComponent>peek</CodeComponent> function which just simply returns the
                value of the first item in the queue, but doesn't <i>remove</i> that item from the queue.
            </li>
        </ul>
    </AccordionItemComponent>)
}

function ProblemSet() {
    return (<ProblemSetComponent dataStructure={Paths.dataStructures.nested.queue.name}>
        <BasicProblems/>
        <AdvancedProblems/>
    </ProblemSetComponent>)
}

function BasicProblems() {
    return (<H3SectionComponent header="Basic Problems" removeTopPadding={true}>
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/design-circular-queue/description/">Design a circular
                queue</LinkComponent></li>
            <li>Given an array of integers <CodeComponent>arr</CodeComponent>, and a window
                size <CodeComponent>n</CodeComponent>, find the moving average of all integers in the sliding window and
                return the results as an array. Ex: arr = [12, 2, 4, 1, 3, 5], n = 3 should return [12, 7, 6, 2.33,
                2.66, 3].
            </li>
        </ul>
    </H3SectionComponent>)
}

function AdvancedProblems() {
    return (<H3SectionComponent header="Advanced Problems">
        <ul>
            <li>Will update when we've covered BFS.</li>
        </ul>
    </H3SectionComponent>)
}
